import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type fastq from 'fastq'
import type { EnhancedDomain, KickPlateBulkOrderItem } from 'metalworks-domain'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AttributeToggleSelector } from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { CustomDistanceDimensionInput } from '../../fields/CustomDistanceDimensionInput'
import { CustomGenericDimensionInput } from '../../fields/CustomGenericDimensionInput'
import { BulkPlateTableRow } from './BulkPlateTableRow'

interface BulkPlateTableProps {
  items: readonly EnhancedDomain.$KickPlateBulkOrderItem[]
  queue: fastq.queue<KickPlateBulkOrderItem>
  imageUrls: Record<string, string | null>
  hasHoles: boolean
  onCreate: () => void
  onDelete: (item: EnhancedDomain.$KickPlateBulkOrderItem) => void
}

export const BulkPlateTable = (props: BulkPlateTableProps) => {
  const { queue, imageUrls, items, hasHoles, onCreate, onDelete } = props

  const [selectedItem, setSelectedItem] = useState<EnhancedDomain.$KickPlateBulkOrderItem | null>(null)
  const itemCountRef = useRef(items.length)

  const handleDelete = useCallback(
    (item: EnhancedDomain.$KickPlateBulkOrderItem) => {
      if (selectedItem?.finalizationId === item.finalizationId) {
        setSelectedItem(null)
      }

      onDelete(item)
    },
    [onDelete, selectedItem]
  )

  // Select new item if it was added.
  useEffect(() => {
    if (items.length > itemCountRef.current) {
      setSelectedItem(items[items.length - 1]!)
    }

    if (items.length !== itemCountRef.current) {
      itemCountRef.current = items.length
    }
  }, [items, items.length])

  useEffect(() => {
    if (!selectedItem && items.length > 0) {
      setSelectedItem(items[0]!)
    }
  }, [items, selectedItem])

  return (
    <Stack direction="row" gap={4} sx={{ height: '100%' }}>
      <TableContainer component={Paper} sx={{ overflowY: 'auto' }}>
        <Table sx={{}}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)' }}>Preview</TableCell>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)' }}>Width</TableCell>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)' }}>Height</TableCell>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)', textAlign: 'right' }}>Horizontal holes</TableCell>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)', textAlign: 'right' }}>Vertical holes</TableCell>
              <TableCell sx={{ width: 'calc((100% - 200px) / 6)', textAlign: 'right' }}>Quantity</TableCell>
              <TableCell sx={{ minWidth: '100px', maxWidth: '100px' }}>Status</TableCell>
              <TableCell sx={{ minWidth: '100px', maxWidth: '100px' }}>&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <BulkPlateTableRow
                key={item.finalizationId}
                queue={queue}
                item={item}
                imageUrl={imageUrls[item.finalizationId] ?? null}
                selected={selectedItem?.finalizationId === item.finalizationId}
                onEdit={setSelectedItem}
                onDelete={handleDelete}
              />
            ))}
          </TableBody>
        </Table>

        <Button variant="text" startIcon={<PhxIcon name="Plus" />} onClick={onCreate}>
          Add Row
        </Button>
      </TableContainer>
      {selectedItem && (
        <Box sx={{ width: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" sx={{ mb: 4 }}>
            Edit properties
          </Typography>

          <PropertyContainer.Group spacing="large">
            <PropertyContainer.Item title="Width">
              <CustomDistanceDimensionInput entityId={selectedItem.id} instanceName="horizontalLength" />
            </PropertyContainer.Item>
            <PropertyContainer.Item title="Height">
              <CustomDistanceDimensionInput entityId={selectedItem.id} instanceName="verticalLength" />
            </PropertyContainer.Item>
            {hasHoles && (
              <>
                <PropertyContainer.Item title="Number of Horizontal Holes">
                  <AttributeToggleSelector entityId={selectedItem.id} instanceName="horizontalHoleCount" />
                </PropertyContainer.Item>
                <PropertyContainer.Item title="Number of Vertical Holes">
                  <AttributeToggleSelector entityId={selectedItem.id} instanceName="verticalHoleCount" />
                </PropertyContainer.Item>
              </>
            )}
            <PropertyContainer.Item title="Quantity">
              <CustomGenericDimensionInput entityId={selectedItem.id} instanceName="quantity" />
            </PropertyContainer.Item>
          </PropertyContainer.Group>

          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mt: 4, alignItems: 'center' }}>
            <PhxIcon name="WarningCircleDuotone" />
            <Typography variant="body2" color="warning">
              All Pricing to be determined in Daveweb Cart
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
